import React from 'react';

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

export default function ContactsInfo({ contacts = [], settings = {}, content = {}, sectionCfg = {} }) {
  const rawBg  = sectionCfg.bg?.startsWith('#') ? sectionCfg.bg : null;
  const theme  = rawBg ? 'custom' : sectionCfg.theme || settings['theme.contact'] || 'light';
  const bg     = rawBg || (theme === 'dark'  ? (settings['color.primary'] || '#0A1628')
               : theme === 'blue'  ? '#0D3B6E'
               : theme.startsWith('#') ? theme
               : '#F4F8FF');
  const isDark = bg === (settings['color.primary']||'#0A1628') || bg === '#0D3B6E' || (bg.startsWith('#') && (() => {
    const r=parseInt(bg.slice(1,3),16); const g=parseInt(bg.slice(3,5),16); const b=parseInt(bg.slice(5,7),16);
    return (r*299+g*587+b*114)/1000 < 128;
  })());

  return (
    <section id="contacts" style={{ padding: '4rem 0', background: bg }}>
      <style>{`
        .contacts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
        }
        .contact-card {
          background: ${isDark ? 'rgba(255,255,255,0.05)' : '#ffffff'};
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'};
          border-radius: 8px;
          padding: 1.2rem;
          display: flex;
          flex-direction: column;
          gap: 10px;
          transition: transform .2s, box-shadow .2s;
        }
        .contact-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        }
        .contact-card-top { display: flex; align-items: flex-start; gap: 10px; }
        .contact-initial {
          width: 38px; height: 38px; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 600; flex-shrink: 0;
          letter-spacing: 0.5px;
        }
        .contact-name { font-size: 13px; font-weight: 600; color: ${isDark ? '#fff' : '#1A2332'}; line-height: 1.3; margin-bottom: 2px; }
        .contact-country { font-size: 11px; color: ${isDark ? 'rgba(255,255,255,0.45)' : '#8A9BB0'}; }
        .contact-divider { height: 1px; background: ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}; }
        .contact-row {
          display: flex; align-items: flex-start; gap: 8px;
          font-size: 12px; color: ${isDark ? 'rgba(255,255,255,0.6)' : '#4A5568'};
          line-height: 1.5;
        }
        .contact-row-icon {
          width: 16px; height: 16px; margin-top: 1px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .contact-row svg { width: 13px; height: 13px; }
        .contact-row a {
          color: inherit; text-decoration: none; word-break: break-word;
        }
        .contact-row a:hover { text-decoration: underline; }
      `}</style>

      <div className="section-inner">
        <div className="section-label" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--color-accent)' }}>
          {content.label || 'Points de souscription'}
        </div>
        <h2 className="section-title" style={{ color: isDark ? '#fff' : undefined }}>
          {content.title || 'Nos interlocuteurs'}
        </h2>
        {content.subtitle && (
          <p className="section-desc" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : undefined }}>
            {content.subtitle}
          </p>
        )}

        {!contacts.length && (
          <div style={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'var(--color-text-mid)', fontSize: 14, padding: '2rem', textAlign: 'center', border: `1px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'var(--color-border)'}`, borderRadius: 6, marginTop: '2rem' }}>
            Aucune fiche contact configurée — ajoutez-en depuis le backoffice.
          </div>
        )}

        <div className="contacts-grid">
          {contacts.map(c => {
            const color   = c.accent_color || '#1B4F8A';
            const rgb     = hexToRgb(color);
            return (
              <div key={c.id} className="contact-card">
                <div className="contact-card-top">
                  <div className="contact-initial" style={{ background: `rgba(${rgb},0.12)`, color }}>
                    {initials(c.name)}
                  </div>
                  <div>
                    <div className="contact-name">{c.name}</div>
                    {c.country && <div className="contact-country">{c.country}</div>}
                  </div>
                </div>

                {(c.address || c.phone || c.email || c.website) && (
                  <div className="contact-divider" />
                )}

                {c.address && (
                  <div className="contact-row">
                    <div className="contact-row-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                    </div>
                    <span>{c.address}</span>
                  </div>
                )}

                {c.phone && (
                  <div className="contact-row">
                    <div className="contact-row-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.13 12 19.79 19.79 0 0 1 1.07 3.4 2 2 0 0 1 3 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z"/>
                      </svg>
                    </div>
                    <a href={`tel:${c.phone.replace(/\s/g,'')}`}>{c.phone}</a>
                  </div>
                )}

                {c.email && (
                  <div className="contact-row">
                    <div className="contact-row-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <a href={`mailto:${c.email}`}>{c.email}</a>
                  </div>
                )}

                {c.website && (
                  <div className="contact-row">
                    <div className="contact-row-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                    </div>
                    <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer">{c.website}</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
